// @ts-strict-ignore
import React, { useMemo, useRef, useState } from 'react';
import { CountMenuReact } from 'common/query-builder/query-block/count-menu/index';
import { getPropertyName, getSelectedCountByLabel } from 'common/query-builder/query-block/count-menu/util';
import { ButtonSize, ButtonTheme } from 'common/components/button/constants';
import { SelectorType } from 'common/query-builder/query-block/constants';
import { useDomEvent } from 'common/hooks/useDomEvent';
import { useTranslation } from 'common/i18n/hooks';
import { IncludeCustomPropertyType } from 'common/query-builder/query-block/properties-menu/types';
import { NewQueryBlock } from '../new-query-block';
type Props = Pick<React.ComponentProps<typeof CountMenuReact>, `advancedMenuSections` | `allDataGroups` | `chartType` | `countUsersOnceState` | `dataGroupId` | `dataGroups` | `dateRange` | `dateUnit` | `enableWorkspaceFilterRestrictions` | `eventNames` | `hideEditControls` | `hideEvents` | `includeCustomProperties` | `metricType` | `onSelect` | `otherAggregationOptions` | `projectHasPeople` | `projectId` | `queryOrigin` | `readonly` | `selected` | `showBorrowedProperties` | `trackName` | `userId` | `workspaceId`> & Pick<React.ComponentProps<typeof NewQueryBlock>, `className` | `elref` | `readonly` | `theme` | `buttonSize`> & {
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  onTrack?: (ev: any) => void;
  /** When true, ignores dataGroupId when generating count labels, showing e.g. "Unique" instead of "Unique Users" */
  withDataGroupSelect?: boolean;
};
export function CountQueryBlock(props: Props) {
  var _props$buttonSize, _props$theme;
  const {
    advancedMenuSections = [],
    allDataGroups,
    chartType,
    className,
    countUsersOnceState,
    dataGroupId,
    dataGroups = [],
    dateRange,
    dateUnit,
    elref,
    enableWorkspaceFilterRestrictions,
    eventNames,
    hideEditControls,
    hideEvents,
    includeCustomProperties = IncludeCustomPropertyType.None,
    metricType,
    onClose,
    onOpen,
    onSelect,
    onTrack,
    open: openProp,
    otherAggregationOptions = [],
    projectHasPeople,
    projectId,
    queryOrigin,
    readonly,
    selected,
    showBorrowedProperties,
    trackName,
    userId,
    withDataGroupSelect = false,
    workspaceId
  } = props;
  const ref = useRef<HTMLDivElement>(null);
  const [open, _setOpen] = useState(openProp);
  const {
    print
  } = useTranslation();
  useDomEvent(ref.current, "track", onTrack);
  const setOpen = (newOpen: boolean) => {
    _setOpen(newOpen);
    if (newOpen) {
      onOpen === null || onOpen === void 0 || onOpen();
    } else {
      onClose === null || onClose === void 0 || onClose();
    }
  };

  // sync with open prop
  if (typeof openProp !== "undefined" && openProp !== open) {
    setOpen(openProp);
  }
  const label = useMemo(() => {
    return getSelectedCountByLabel({
      metricType,
      selected,
      dataGroupId,
      dataGroups,
      withDataGroupSelect
    });
  }, [metricType, selected, dataGroupId, dataGroups, withDataGroupSelect]);
  const selectedPropertyName = print(getPropertyName(selected.property, {
    includeListItemGroup: true
  }));
  return <NewQueryBlock ref={ref} label={label} browserTooltip={label} labelHighlightSelections={[selectedPropertyName]} buttonSize={(_props$buttonSize = props.buttonSize) !== null && _props$buttonSize !== void 0 ? _props$buttonSize : ButtonSize.Small} className={className} elref={elref} open={open} onChangeOpen={setOpen} readonly={readonly} selectorType={SelectorType.Button} theme={(_props$theme = props.theme) !== null && _props$theme !== void 0 ? _props$theme : ButtonTheme.QueryBuilder} rightIcon='caret-down' menuElement={<CountMenuReact chartType={chartType} dataGroups={dataGroups} dateRange={dateRange} dateUnit={dateUnit} advancedMenuSections={advancedMenuSections} countUsersOnceState={countUsersOnceState} otherAggregationOptions={otherAggregationOptions} dataGroupId={dataGroupId} metricType={metricType} open={open} selected={selected} trackName={trackName} enableWorkspaceFilterRestrictions={enableWorkspaceFilterRestrictions} includeCustomProperties={includeCustomProperties} showBorrowedProperties={showBorrowedProperties} allDataGroups={allDataGroups} hideEditControls={hideEditControls} hideEvents={hideEvents} projectHasPeople={projectHasPeople} eventNames={eventNames} projectId={projectId} queryOrigin={queryOrigin} readonly={readonly} userId={userId} workspaceId={workspaceId} onSelect={item => {
    setOpen(false);
    onSelect(item);
  }} />} data-sentry-element="NewQueryBlock" data-sentry-component="CountQueryBlock" data-sentry-source-file="count-query-block.tsx" />;
}